import type { MapareRow } from '@lyra/shared';

export type NoMatchKind = 'no_match' | 'low_confidence';

/** Effective no-match kind, tolerant of legacy rows that only carry a reason message. */
export function noMatchKind(row: MapareRow): NoMatchKind | null {
  if (row.workflowStatus === 'low_confidence') return 'low_confidence';
  if (row.workflowStatus !== 'no_match') return null;
  const reason = (row.ambiguityReason ?? '').toLowerCase();
  if (reason.includes('similarity threshold') || reason.includes('low_confidence')) {
    return 'low_confidence';
  }
  return 'no_match';
}

export type WizardBucket =
  | 'confirmat_rezolvat' | 'ambiguu' | 'revizuire'
  | 'low_confidence' | 'no_match' | 'eroare' | 'done';

export function wizardBucket(row: MapareRow): WizardBucket {
  if (row.status === 'error') return 'eroare';
  if (row.status === 'pending') return 'done';

  const nm = noMatchKind(row); // 'no_match' | 'low_confidence' | null

  // Single-step (judgement) buckets: done once the operator has acted on the row.
  const handled = row.reviewResolution != null || row.validatedAt != null || row.status === 'edited';
  if (row.workflowStatus === 'ambiguous') return handled ? 'done' : 'ambiguu';
  if (row.workflowStatus === 'needs_review') return handled ? 'done' : 'revizuire';
  if (nm === 'low_confidence') return handled ? 'done' : 'low_confidence';

  // List (bulk) buckets: done only once the operator signed off (validated_at) or edited.
  // Pipeline-set reviewResolution does NOT count as done here.
  const listDone = row.validatedAt != null || row.status === 'edited';
  if (row.workflowStatus === 'resolved') return listDone ? 'done' : 'confirmat_rezolvat';
  if (nm === 'no_match') return listDone ? 'done' : 'no_match';

  return 'done';
}

/** Ordered wizard steps. `mode` drives which step component renders. */
export const WIZARD_STEPS = [
  { id: 'confirmat_rezolvat', label: 'Confirmate / Rezolvate', mode: 'list'         as const },
  { id: 'ambiguu',            label: 'Ambigue',                mode: 'single'       as const },
  { id: 'revizuire',          label: 'Revizuire',              mode: 'masterdetail' as const },
  { id: 'low_confidence',     label: 'Încredere scăzută',      mode: 'masterdetail' as const },
  { id: 'no_match',           label: 'Fără corespondent',      mode: 'list'         as const },
  { id: 'eroare',             label: 'Erori',                  mode: 'list'         as const },
  { id: 'sumar',              label: 'Sumar',                  mode: 'summary'      as const },
] as const;

export type WizardStepId = (typeof WIZARD_STEPS)[number]['id'];

/**
 * Steps that represent unfinished operator judgement — every step except
 * `confirmat_rezolvat` (pre-resolved rows only waiting on a bulk-confirm
 * click, not "cases to review") and `sumar` itself. Order matches
 * `WIZARD_STEPS`, i.e. the wizard nav's natural left-to-right progression.
 * Used by `WizardSummary`'s peak-end fix (Impeccable critique P1): summing
 * these buckets gives "remaining", and the first one that's still non-empty
 * is the forward CTA's target.
 */
export const UNRESOLVED_STEP_IDS: readonly WizardStepId[] = WIZARD_STEPS.filter(
  (s) => s.mode !== 'summary' && s.id !== 'confirmat_rezolvat'
).map((s) => s.id);

/** Sum of unresolved-bucket counts (`UNRESOLVED_STEP_IDS`) — "remaining" for the Sumar step. */
export function remainingCount(counts: Record<string, number>): number {
  return UNRESOLVED_STEP_IDS.reduce((n, id) => n + (counts[id] ?? 0), 0);
}

/**
 * First unresolved step (in wizard nav order) that still has rows, or `null`
 * once every unresolved bucket is empty (in which case `remainingCount` is
 * 0 too). Returns the full `WIZARD_STEPS` entry so callers get both `id`
 * (for the route) and `label` (for the CTA copy) without a second lookup.
 */
export function firstUnresolvedStep(
  counts: Record<string, number>
): (typeof WIZARD_STEPS)[number] | null {
  return (
    WIZARD_STEPS.find(
      (s) => UNRESOLVED_STEP_IDS.includes(s.id) && (counts[s.id] ?? 0) > 0
    ) ?? null
  );
}
